import { enableProdMode, LOCALE_ID, ErrorHandler, APP_INITIALIZER, Injector, importProvidersFrom } from "@angular/core";

import { appInit } from "./app/app.module";
import { environment } from "./environments/environment";
import { CookieService } from "ngx-cookie-service";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { EforsyningHttpInterceptor } from "@e-forsyning/common/http";
import { ENV, LOGGER, LogentriesLoggerService } from "@dffedb/util";
import { EforsyningErrorHandler, GlobalErrorListenerService } from "@e-forsyning/common/error";
import { Router, NavigationEnd } from "@angular/router";
import { EBoksPopupService, EBoksTilmeldingModule } from "@e-forsyning/common/e-boks";
import { KontaktoplysningerPopupService, KontaktoplysningerPopupModule } from "@e-forsyning/common/kontaktoplysninger-popup";
import { AktuelBrugersInstallationerService } from "@e-forsyning/common/installation/aktuel-brugers-installationer.service";
import { filter, first } from "rxjs/operators";
import { RouteBodyClassService, RolleBodyClassService } from "@e-forsyning/common/util";
import { EforsyningSidenavMenuModule } from "@e-forsyning/common/eforsyning-menu";
import { AppShellModule } from "@e-forsyning/common/app-shell";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { PushNotificationService, TaskbarBadgeService } from "@e-forsyning/common/push-notification";
import { AppRoutingModule } from "./app/app-routing.module";
import { provideAnimations } from "@angular/platform-browser/animations";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDffDateModule, DffSimpelDialogModule } from "@dffedb/ui";
import { EForsyningFeaturesDefaultModule } from "@e-forsyning/features/default";
import { EForsyningLogindKnapModule, DffedbCookieConsentModule, DffedbDriftsstatusKnapModule, DffedbFancyboxModule } from "@e-forsyning/web-components";
import { provideServiceWorker } from "@angular/service-worker";
import { AppComponent } from "./app/app.component";
import { provideMatomo, withRouter } from "ngx-matomo-client";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { MatomoTracker } from "ngx-matomo-client";
import { LogService } from "@e-forsyning/common/log";

function initBodyClassServices(injector: Injector): void {
    injector.get(RouteBodyClassService).setup();
    injector.get(RolleBodyClassService).setup();
}

function initPopups(injector: Injector): void {
    const router = injector.get(Router);
    const eboksPopupService = injector.get(EBoksPopupService);
    const kontaktoplysningerPopupService = injector.get(KontaktoplysningerPopupService);
    const aktuelBrugersInstallationerService = injector.get(AktuelBrugersInstallationerService);

    router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(async () => {
        const installationer = await aktuelBrugersInstallationerService.select().pipe(first()).toPromise();
        if (installationer.length === 1) {
            eboksPopupService.aabnHvisIkkeAlleredeTilmeldt().then(() => {
                kontaktoplysningerPopupService.aabnHvisIkkeAlleredeTilmeldt();
            });
        }
    });
}

// Initialize Matomo with the correct site ID and update ngx-matomo-client
function initMatomoWithCorrectSiteId(injector: Injector): () => Promise<void> {
    return async () => {
        const umbracoIndstillingerObserver = injector.get(UmbracoIndstillingerObserver);
        const matomoTracker = injector.get(MatomoTracker);

        // Wait for UmbracoIndstillinger to be loaded
        const settings = await umbracoIndstillingerObserver.getValue();
        const siteId = settings?.matomoSiteId ? parseInt(settings.matomoSiteId, 10) : 2334;

        console.log(`Matomo: UmbracoIndstillinger loaded with site ID: ${siteId}`);

        // Update the global Matomo configuration to use the correct site ID
        if ((window as any)._paq) {
            (window as any)._paq.push(["setSiteId", siteId]);
            console.log(`Matomo: Updated site ID to: ${siteId}`);
        } else {
            console.warn(`Matomo: _paq not found, ngx-matomo-client may not be initialized yet`);
        }

        // Track PWA status (event tracking for app launch)
        trackPWAStatus(matomoTracker);

        // Initialize app type tracking for all page views via events
        initAppTypeTracking(injector);
    };
}

// Track PWA status (event tracking for app launch - one time per session)
function trackPWAStatus(matomoTracker: MatomoTracker): void {
    // Check if we've already tracked launch this session
    const launchTracked = sessionStorage.getItem("matomo_launch_tracked");
    if (launchTracked) {
        return; // Already tracked this session
    }

    // Check if running in PWA mode
    const isPWA = isPWAOrTWA();

    // Track the launch type (PWA or Web)
    if (isPWA) {
        matomoTracker.trackEvent("Application", "App Launch", "PWA Launch");
    } else {
        matomoTracker.trackEvent("Application", "App Launch", "Web Launch");
    }

    // Mark as tracked for this session
    sessionStorage.setItem("matomo_launch_tracked", "true");
}

// Detect if running in PWA/TWA mode
function isPWAOrTWA(): boolean {
    return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone || document.referrer.includes("android-app://");
}

// Track app type (PWA or Browser) via event tracking at each page view
// This provides accurate tracking on Android PWA where APP_INITIALIZER only runs once
function trackAppTypePageView(matomoTracker: MatomoTracker): void {
    const isPWA = isPWAOrTWA();
    const appType = isPWA ? "PWA" : "Browser";

    // Track event for each page view with app type
    // Category: "Page View", Action: "App Type", Name: "PWA" or "Browser"
    // This allows tracking how many page views come from PWA vs Browser in Matomo Events report
    matomoTracker.trackEvent("Page View", "App Type", appType);
}

// Initialize app type tracking for all page views via events
// This listens to router navigation events and tracks app type at each navigation
function initAppTypeTracking(injector: Injector): void {
    const router = injector.get(Router);
    const matomoTracker = injector.get(MatomoTracker);

    // Track on initial page load
    trackAppTypePageView(matomoTracker);

    // Listen to all navigation events and track app type at each page view
    router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        // Track app type for each page view
        // This ensures accurate tracking on Android PWA where navigation doesn't trigger app restart
        trackAppTypePageView(matomoTracker);
    });
}

function initGlobalErrorListeners(globalErrorListenerService: GlobalErrorListenerService): () => void {
    return () => {
        globalErrorListenerService.init();
    };
}

function initStartupDiagnostics(injector: Injector): () => void {
    return () => {
        const logService = injector.get(LogService);
        const router = injector.get(Router);
        const startedAt = Date.now();
        let bootCompleted = false;

        const logMilestone = (milestone: string, details?: Record<string, unknown>): void => {
            const beskrivelse = `BOOT: ${milestone}`;
            const detaljer = details ? JSON.stringify(details) : undefined;

            try {
                logService.log({
                    Besked: beskrivelse,
                    Detaljer: detaljer
                });
            } catch {
                // Keep diagnostics fully passive if logging itself fails.
            }

            console.log(`[BootDiagnostics] ${beskrivelse}`);
        };

        logMilestone("app_initializer_started", {
            href: window.location.href,
            userAgent: window.navigator.userAgent
        });

        router.events.pipe(filter((e) => e instanceof NavigationEnd), first()).subscribe((event) => {
            bootCompleted = true;
            const navigationEvent = event as NavigationEnd;

            logMilestone("first_navigation_end", {
                durationMs: Date.now() - startedAt,
                urlAfterRedirects: navigationEvent.urlAfterRedirects
            });
        });

        setTimeout(() => {
            if (bootCompleted) {
                return;
            }

            const durationMs = Date.now() - startedAt;
            const timeoutError = new Error(`Boot timeout: first navigation did not complete within 15000ms (actual: ${durationMs}ms)`);

            console.error(`[BootDiagnostics] ${timeoutError.message}`);
            logMilestone("boot_timeout", {
                durationMs,
                href: window.location.href
            });
            logService.logError(timeoutError).catch(() => {
                // Keep diagnostics fully passive if logging itself fails.
            });
        }, 15000);
    };
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            AppShellModule,
            BrowserModule,
            AppRoutingModule,
            HttpClientModule,
            MatButtonModule,
            MatIconModule,
            MatSnackBarModule,
            MatDffDateModule,
            DffSimpelDialogModule,
            EForsyningFeaturesDefaultModule,
            EforsyningSidenavMenuModule,
            EBoksTilmeldingModule,
            KontaktoplysningerPopupModule,
            EForsyningLogindKnapModule,
            DffedbCookieConsentModule,
            DffedbDriftsstatusKnapModule,
            DffedbFancyboxModule
        ),
        CookieService,
        {
            provide: LOCALE_ID,
            useValue: "da"
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: EforsyningHttpInterceptor,
            multi: true
        },
        {
            provide: ENV,
            useValue: environment
        },
        {
            provide: LOGGER,
            useClass: LogentriesLoggerService
        },
        {
            provide: ErrorHandler,
            useClass: EforsyningErrorHandler
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInit,
            deps: [Injector],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initMatomoWithCorrectSiteId,
            deps: [Injector],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initGlobalErrorListeners,
            deps: [GlobalErrorListenerService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initStartupDiagnostics,
            deps: [Injector],
            multi: true
        },
        provideMatomo(
            {
                siteId: 2334, // Initial site ID, will be updated by initMatomoWithCorrectSiteId
                trackerUrl: environment.matomoUrl
            },
            withRouter()
        ),
        AktuelBrugersInstallationerService,
        PushNotificationService,
        TaskbarBadgeService,
        provideAnimations(),
        // Service Worker konfiguration
        // Service Worker kan registreres på localhost med HTTPS hvis certifikatet er accepteret
        // For Windows PWA skal Service Worker være aktiveret for at Web Push API kan virke
        // VIGTIGT: Deaktiver Service Worker i Capacitor native apps (de bruger native push notifications)
        provideServiceWorker("/ngsw-worker.js", {
            enabled: (() => {
                if (typeof window === "undefined") {
                    return false;
                }

                // Deaktiver Service Worker i Capacitor native apps
                // Capacitor apps bruger native push notifications og har ikke brug for Service Worker
                // Dette undgår også problemer med Service Worker der intercepterer app server requests
                // Tjek om Capacitor er tilgængelig og om vi er i en native app
                if (typeof (window as any).Capacitor !== "undefined") {
                    const Capacitor = (window as any).Capacitor;
                    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
                        return false;
                    }
                }

                const hostname = window.location.hostname;

                // Tillad Service Worker for localhost med HTTPS (fx https://localhost:51144)
                // Dette virker hvis certifikatet er accepteret i browseren
                const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
                const isHttps = window.location.protocol === "https:";

                // Hvis det er localhost med HTTPS, tillad Service Worker (for Windows PWA support)
                if (isLocalhost && isHttps) {
                    return true;
                }

                // For IP adresser, deaktiver (kan have problemer med selvsignerede certifikater)
                if (hostname.match(/^172\.|^192\.168\.|^10\./)) {
                    return false;
                }

                // For alle andre domæner (fx dev-eforsyning.softvaerket.dk), aktiver
                return true;
            })(),
            // iOS Safari kræver at Service Worker registreres manuelt for at undgå CORS problemer
            // Brug "registerImmediately" for iOS, ellers "registerWhenStable" for bedre performance
            registrationStrategy: typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) ? "registerImmediately" : "registerWhenStable:30000",
            scope: "/"
        })
    ]
}).catch((err) => console.error(err));
